import React from 'react';
import { useT } from '../BuilderApp.jsx';
import WizardShell from '../WizardShell.jsx';
import { tmplById } from '../../data/defaults.js';

function StepNextHint({ step }) {
  const t = useT();
  const msg = t[`next${step}`];
  if (!msg) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:'.4rem',justifyContent:'center',marginTop:'.55rem',fontSize:'.72rem',color:'rgba(148,163,184,.55)',letterSpacing:'.01em',lineHeight:1.4}}>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{flexShrink:0,opacity:.55}}>
        <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{msg}</span>
    </div>
  );
}

export default function Step4({ config, setField, next, back, lang, setLang }) {
  const t = useT();
  const upd = (k, v) => setField('content', { ...config.content, [k]: v });
  const tmpl = tmplById(config.template);
  const primary = config.branding.primary || '#3b82f6';
  const secondary = config.branding.secondary || '#6366f1';
  const name = config.branding.company_name || (lang === 'no' ? 'Din bedrift' : 'Your Business');
  const headline = config.content.hero_headline || (lang === 'no' ? 'Velkommen!' : 'Welcome!');
  const sub = config.content.hero_sub || (lang === 'no' ? 'Vi ser frem til å betjene deg.' : 'We look forward to serving you.');
  const logoSrc = config.branding.logo_dataUrl || config.branding.logo_url || null;

  const slugPreview = name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

  return (
    <WizardShell step={4} total={8} title={t.s4_title} subtitle={t.s4_sub} onBack={back} lang={lang} setLang={setLang}>
      <div style={{display:'flex',flexDirection:'column',gap:'1.25rem'}}>

        {/* Live mini-preview */}
        <div style={{borderRadius:14,overflow:'hidden',border:'1px solid var(--border)',marginBottom:'.25rem'}}>
          {/* Browser chrome */}
          <div style={{background:'#1a2030',padding:'.5rem .75rem',display:'flex',alignItems:'center',gap:'.4rem',borderBottom:'1px solid var(--border)'}}>
            <div style={{display:'flex',gap:'.3rem'}}>
              {['#f87171','#fbbf24','#34d399'].map(c => <div key={c} style={{width:8,height:8,borderRadius:'50%',background:c}}/>)}
            </div>
            <div style={{flex:1,background:'rgba(255,255,255,.06)',borderRadius:4,padding:'.2rem .5rem',fontSize:'.6rem',color:'var(--dim)',textAlign:'center',maxWidth:200,margin:'0 auto',overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
              {slugPreview || 'mybusiness'}.chamcore.site
            </div>
          </div>
          {/* Simulated hero */}
          <div style={{background:`linear-gradient(135deg,${primary}cc,${secondary}99)`,padding:'1.5rem',position:'relative',minHeight:110}}>
            <div style={{display:'flex',alignItems:'center',gap:'.5rem',marginBottom:'.65rem'}}>
              {logoSrc
                ? <img src={logoSrc} alt="logo" style={{height:22,maxWidth:80,objectFit:'contain',borderRadius:3,background:'rgba(255,255,255,.1)'}}/>
                : <div style={{fontWeight:900,fontSize:'.75rem',color:'#fff',letterSpacing:'-.01em'}}>{name}</div>
              }
            </div>
            <div style={{fontWeight:900,fontSize:'clamp(.9rem,3vw,1.15rem)',color:'#fff',marginBottom:'.3rem',lineHeight:1.2}}>
              {headline}
            </div>
            <div style={{fontSize:'.7rem',color:'rgba(255,255,255,.75)',lineHeight:1.4,maxWidth:'80%'}}>{sub}</div>
            <div style={{display:'inline-flex',alignItems:'center',marginTop:'.65rem',background:'#fff',borderRadius:6,padding:'.3rem .75rem',fontSize:'.65rem',fontWeight:700,color:primary,gap:'.3rem'}}>
              {lang === 'no' ? 'Book time →' : 'Book now →'}
            </div>
          </div>
          <div style={{background:'#111827',padding:'.5rem .75rem',fontSize:'.6rem',color:'var(--dim)',textAlign:'center'}}>
            {lang === 'no' ? '✓ Forhåndsvisning oppdateres mens du skriver' : '✓ Preview updates as you type'}
          </div>
        </div>

        {/* Form fields */}
        <div>
          <label className="field-label">{t.s4_headline}</label>
          <input
            type="text"
            className="field-input"
            placeholder={lang === 'no'
              ? `f.eks. "Beste ${tmpl.label.toLowerCase()} i byen"`
              : `e.g. "The best ${tmpl.label.toLowerCase()} in town"`}
            value={config.content.hero_headline}
            onChange={e => upd('hero_headline', e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">{t.s4_subheadline}</label>
          <input
            type="text"
            className="field-input"
            placeholder={t.s4_sub_ph}
            value={config.content.hero_sub}
            onChange={e => upd('hero_sub', e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">{t.s4_about}</label>
          <textarea
            className="field-input"
            rows={3}
            placeholder={t.s4_about_ph}
            value={config.content.about}
            onChange={e => upd('about', e.target.value)}
            style={{resize:'vertical'}}
          />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,200px),1fr))',gap:'1rem'}}>
          <div>
            <label className="field-label">{t.s4_email}</label>
            <input
              type="email"
              className="field-input"
              placeholder="hello@you.com"
              value={config.content.contact_email}
              onChange={e => upd('contact_email', e.target.value)}
            />
          </div>
          <div>
            <label className="field-label">{t.s4_phone}</label>
            <input
              type="tel"
              className="field-input"
              placeholder="+47 12 345 678"
              value={config.content.contact_phone}
              onChange={e => upd('contact_phone', e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="field-label">{t.s4_address}</label>
          <input
            type="text"
            className="field-input"
            placeholder={t.s4_address_ph}
            value={config.content.address}
            onChange={e => upd('address', e.target.value)}
          />
        </div>
        <button className="btn btn-primary btn-lg btn-full" onClick={next}>{t.continue}</button>
        <StepNextHint step={4}/>
      </div>
    </WizardShell>
  );
}
